import React, { useContext, useEffect, useState } from 'react';
import moment, { Moment } from 'moment';

import ReportDateRange from '../../../components/ui/ReportDateRange';
import { downloadCsv } from '../../../utils/csv';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/auth/AuthContext';
import { DISTRIBUTION_LABELS, loadTipReport, reportCsvRows, reportTotals, TipReport } from './tips';

const money = (value: number) => Number(value ?? 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Reporte de propinas y cargo por servicio por mesero, con el reparto. La configuración está en
// Configuraciones > Propinas (TipSettingsPage).
export const TipsPage = () => {
    const navigate = useNavigate();
    const { resources } = useContext(AuthContext);
    const canConfigure = resources.some(r => r.code === 'tips/settings');

    // Reporte
    const [range, setRange] = useState<{ from: string; to: string }>({ from: moment().format('YYYY-MM-DD'), to: moment().format('YYYY-MM-DD') });
    const [report, setReport] = useState<TipReport | null>(null);
    const [loadingReport, setLoadingReport] = useState(true);

    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        setLoadingReport(true);
        setError('');
        loadTipReport(range.from, range.to)
            .then(r => active && setReport(r))
            .catch(e => active && setError(e.message))
            .finally(() => active && setLoadingReport(false));
        return () => { active = false; };
    }, [range]);

    const handleRange = (start: Moment, end: Moment) => setRange({ from: start.format('YYYY-MM-DD'), to: end.format('YYYY-MM-DD') });

    const totals = reportTotals(report?.rows ?? []);

    const reportTab = (
        <div className="card border-0 shadow-sm">
            <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
                    <div>
                        <label className="form-label small text-muted mb-1">Fechas</label>
                        <ReportDateRange onChange={handleRange} minDate={moment().subtract(1, 'year')} maxSpan={{ months: 3 }} />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {report && <span className="badge bg-light text-dark border">Reparto: {DISTRIBUTION_LABELS[report.distribution].title}</span>}
                        <button type="button" className="btn btn-outline-secondary" disabled={!report?.rows.length}
                            onClick={() => report && downloadCsv(`propinas_${report.from}_${report.to}.csv`, reportCsvRows(report))}>
                            <i className="bi bi-download me-1" /> Exportar
                        </button>
                    </div>
                </div>

                <div className="row g-3 mb-3">
                    {[
                        { label: 'Propinas', value: totals.tips, icon: 'bi-cash-coin' },
                        { label: 'Servicio y otros cargos', value: totals.service, icon: 'bi-receipt' },
                        { label: 'Total a repartir', value: totals.tips + totals.service, icon: 'bi-people' },
                    ].map(card => (
                        <div key={card.label} className="col-12 col-md-4">
                            <div className="border rounded p-3 h-100">
                                <div className="small text-muted"><i className={`bi ${card.icon} me-1`} />{card.label}</div>
                                <div className="fs-4 fw-semibold">{money(card.value)}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {loadingReport ? (
                    <div className="text-center text-muted py-4"><span className="spinner-border spinner-border-sm me-2" />Cargando...</div>
                ) : !report || report.rows.length === 0 ? (
                    <div className="text-center text-muted py-4">No hay facturas en estas fechas.</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th>Mesero</th>
                                    <th className="text-end">Facturas</th>
                                    <th className="text-end">Total facturado</th>
                                    <th className="text-end">Propinas</th>
                                    <th className="text-end">Servicio y otros cargos</th>
                                    <th className="text-end">Le corresponde</th>
                                </tr>
                            </thead>
                            <tbody>
                                {report.rows.map(r => (
                                    <tr key={r.waiterId ?? 'none'} className={r.waiterId ? '' : 'text-muted'}>
                                        <td>{r.waiter}</td>
                                        <td className="text-end">{r.invoices}</td>
                                        <td className="text-end">{money(r.sales)}</td>
                                        <td className="text-end">{money(r.tips)}</td>
                                        <td className="text-end">{money(r.service)}</td>
                                        <td className="text-end fw-semibold">{money(r.payout)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="fw-semibold">
                                    <td>Total</td>
                                    <td className="text-end">{totals.invoices}</td>
                                    <td className="text-end">{money(totals.sales)}</td>
                                    <td className="text-end">{money(totals.tips)}</td>
                                    <td className="text-end">{money(totals.service)}</td>
                                    <td className="text-end">{money(totals.payout)}</td>
                                </tr>
                            </tfoot>
                        </table>
                        <div className="small text-muted">
                            Se excluyen las facturas anuladas. "Servicio y otros cargos" son los otros cargos de la factura sin la propina.
                            {report.distribution === 'FONDO' && ' En fondo común, lo de las facturas sin mesero también se reparte.'}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-3 gap-2">
                <div>
                    <h3 className="mb-1">Propinas</h3>
                    <p className="text-muted mb-0">Propinas y cargo por servicio por mesero, y cuánto le corresponde a cada uno.</p>
                </div>
                {canConfigure && (
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/tips/settings')}>
                        <i className="bi bi-gear me-1" /> Configuración
                    </button>
                )}
            </div>

            {error && (
                <div className="alert alert-danger d-flex align-items-start gap-2 py-2">
                    <i className="bi bi-exclamation-circle mt-1" />
                    <div className="small">{error}</div>
                </div>
            )}
            {reportTab}
        </div>
    );
};
